import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * How long to suppress the update prompt after the user clicks Dismiss.
 * After this window the prompt re-surfaces automatically, so a tab
 * that's been left open all day doesn't stay stuck on a stale build
 * just because the user accidentally hit X. The SW itself is also
 * re-checked on every page load (registerSW does an `update()` on
 * mount), so reloading at any point also brings the prompt back.
 */
const SNOOZE_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Surfaces a "New version available" prompt whenever the PWA service
 * worker has finished installing a fresh build in the background.
 *
 * Pairs with `registerType: 'prompt'` in vite.config.ts: the new SW
 * installs but does not auto-skipWaiting, so the page keeps serving
 * the previously-cached assets until the user explicitly accepts the
 * update. Hitting "Update" calls `updateServiceWorker(true)`, which
 * skipWaitings the new SW and reloads the page so the latest build
 * actually loads instead of staying behind the old in-memory bundle.
 *
 * Dismissing snoozes the prompt for SNOOZE_MS instead of permanently
 * hiding it — guarantees the user eventually sees the new build even
 * if they never reload the tab.
 *
 * The toast lives bottom-right so it doesn't collide with the app-
 * level GlobalToast (which is bottom-centre and short-lived).
 */
export function UpdateToast() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error) {
      // Registration failures shouldn't take the app down — log and
      // continue without the prompt UX. (Most common cause: running
      // under http:// in dev with the SW disabled, in which case
      // there's nothing to surface anyway.)
      // eslint-disable-next-line no-console
      console.warn('PWA service worker registration failed:', error);
    },
    onRegisteredSW(_swUrl, registration) {
      // Without an explicit poll, a tab left open all day only
      // discovers a new build when the browser independently
      // revalidates `sw.js` (which it does on focus / nav). Backgrounded
      // tabs in particular can sit on stale code for hours. Hourly
      // `update()` is cheap (one HEAD-style request to a `no-cache`
      // resource) and means the prompt surfaces within at most an hour
      // of a deploy even on tabs the user never refreshed.
      if (!registration) return;
      const HOUR = 60 * 60 * 1000;
      const id = window.setInterval(() => {
        // Only poll when the document is visible — no point hitting
        // the network while the tab is in the background, the next
        // visibility-change will trigger an update check anyway.
        if (document.visibilityState !== 'visible') return;
        registration.update().catch(() => {
          /* network blip — try again next interval */
        });
      }, HOUR);
      // Stash on registration so a hot-reload re-run cleans up; the
      // window object can't easily hold this and we can't return a
      // cleanup from `onRegisteredSW` (it isn't a hook).
      (registration as unknown as { _ahPollId?: number })._ahPollId = id;
    },
  });

  const snoozeRef = useRef<number | null>(null);

  // Cancel any pending snooze when the component unmounts (tab close
  // or navigation tears the timeout down anyway, but this keeps the
  // hook contract clean and avoids the "setState on unmounted" warning
  // if React unmounts us mid-snooze).
  useEffect(
    () => () => {
      if (snoozeRef.current !== null) {
        window.clearTimeout(snoozeRef.current);
        snoozeRef.current = null;
      }
    },
    []
  );

  // Re-bring-up the prompt after the snooze window. We only schedule
  // when the user explicitly dismisses; an Update click never queues
  // a snooze (that path navigates away). If a fresh `needRefresh =
  // true` arrives before the timer fires (e.g., the SW publishes a
  // newer-still build), we cancel + re-schedule from scratch.
  const dismiss = () => {
    setNeedRefresh(false);
    if (snoozeRef.current !== null) window.clearTimeout(snoozeRef.current);
    snoozeRef.current = window.setTimeout(() => {
      snoozeRef.current = null;
      setNeedRefresh(true);
    }, SNOOZE_MS);
  };

  return (
    <AnimatePresence>
      {needRefresh && (
        <motion.div
          key="pwa-update-toast"
          initial={{ y: 24, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 12, opacity: 0, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          className="fixed bottom-6 right-6 z-[110] max-w-[calc(100vw-3rem)]"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-3 rounded-2xl border border-accent/40 bg-bg-panel/95 px-4 py-3 shadow-[0_24px_64px_-16px_rgb(0_0_0_/_0.55)] backdrop-blur-xl">
            <span
              aria-hidden
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent text-accent-contrast shadow-glow"
            >
              <Sparkles size={15} strokeWidth={2.5} />
            </span>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-fg">
                New version available
              </span>
              <span className="text-[11px] text-fg-muted">
                Refresh to load the latest build.
              </span>
            </div>
            <button
              type="button"
              onClick={() => updateServiceWorker(true)}
              className="ml-2 inline-flex h-8 items-center rounded-lg bg-accent px-3 text-xs font-semibold text-accent-contrast shadow-glow hover:opacity-90 focus-ring"
            >
              Update
            </button>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss update prompt"
              title="Dismiss for now (we'll remind you in 15 minutes)"
              className="grid h-7 w-7 place-items-center rounded-full text-fg-muted hover:text-fg focus-ring"
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
